import { SalePaymentLineDialog } from "@/components/sales/SalePaymentLineDialog";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { fetchBanks } from "@/lib/sales/banksStore";
import {
  newSalePaymentLine,
  paymentMethodLabel,
  salePaymentLineReference,
  salePaymentLinesTotal,
  validateSalePaymentLine,
  type SalePaymentLine,
} from "@/lib/sales/salePaymentLines";
import { cn } from "@/lib/utils";
import { Edit, Plus, Trash2, Wallet } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

type Props = {
  lines: SalePaymentLine[];
  onChange: (lines: SalePaymentLine[]) => void;
  expectedTotal: number;
  salePrice: number;
  disabled?: boolean;
  className?: string;
  title?: string;
  emptyTitle?: string;
  emptyHint?: string;
  addButtonLabel?: string;
  removeDescription?: string;
};

function emptyModalLine(remaining: number): SalePaymentLine {
  return newSalePaymentLine(remaining > 0 ? remaining : 0);
}

export function SalePaymentLinesSection({
  lines,
  onChange,
  expectedTotal,
  salePrice,
  disabled = false,
  className,
  title = "Payment methods",
  emptyTitle = "No payment lines added.",
  emptyHint = 'Click "Add payment line" to record cash, online, cheque, or other methods.',
  addButtonLabel = "Add payment line",
  removeDescription = "This payment method line will be removed from the sale.",
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalLine, setModalLine] = useState<SalePaymentLine>(() => emptyModalLine(0));
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);

  const linesTotal = useMemo(() => salePaymentLinesTotal(lines), [lines]);
  const remaining = Math.max(0, Math.round((expectedTotal - linesTotal) * 100) / 100);
  const totalsMatch = expectedTotal <= 0 || Math.abs(linesTotal - expectedTotal) < 0.01;
  const exceedsSalePrice = salePrice > 0 && linesTotal > salePrice + 0.009;

  const modalOtherLinesTotal =
    editingIndex != null
      ? salePaymentLinesTotal(lines.filter((_, index) => index !== editingIndex))
      : linesTotal;

  const openAddModal = () => {
    setEditingIndex(null);
    setModalLine(emptyModalLine(Math.max(0, expectedTotal - linesTotal)));
    setModalOpen(true);
  };

  const openEditModal = (index: number) => {
    const line = lines[index];
    if (!line) return;
    setEditingIndex(index);
    setModalLine({ ...line });
    setModalOpen(true);
  };

  const resolveBankName = async (bankId: string): Promise<string> => {
    if (!bankId.trim()) return "";
    try {
      const banks = await fetchBanks();
      return banks.find((bank) => String(bank.id) === bankId)?.name ?? "";
    } catch {
      return "";
    }
  };

  const saveModalLine = async (andAddMore = false) => {
    const otherTotal =
      editingIndex != null
        ? salePaymentLinesTotal(lines.filter((_, index) => index !== editingIndex))
        : linesTotal;
    const error = validateSalePaymentLine(modalLine, {
      maxAmount: salePrice > 0 ? salePrice : expectedTotal,
      otherLinesTotal: otherTotal,
    });
    if (error) {
      toast.error(error);
      return;
    }

    const bankName = await resolveBankName(modalLine.bankId);
    const saved: SalePaymentLine = { ...modalLine, bankName };

    const next = [...lines];
    if (editingIndex != null && editingIndex >= 0 && editingIndex < next.length) {
      next[editingIndex] = saved;
    } else {
      next.push(saved);
    }
    onChange(next);

    if (andAddMore && editingIndex == null) {
      toast.success("Payment line added.");
      const newTotal = salePaymentLinesTotal(next);
      setModalLine(emptyModalLine(Math.max(0, expectedTotal - newTotal)));
      return;
    }

    toast.success(editingIndex != null ? "Payment line updated." : "Payment line added.");
    setModalOpen(false);
  };

  const confirmRemove = () => {
    if (removeIndex == null) return;
    onChange(lines.filter((_, index) => index !== removeIndex));
    setRemoveIndex(null);
    toast.success("Payment line removed.");
  };

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
          {expectedTotal > 0 ? (
            <p
              className={cn(
                "mt-0.5 text-xs tabular-nums",
                exceedsSalePrice || !totalsMatch ? "font-medium text-destructive" : "text-muted-foreground",
              )}
            >
              Allocated {getActiveCompanyCurrency().symbol}{" "}
              {linesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {" / "}
              {expectedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {exceedsSalePrice ? " — cannot exceed total" : !totalsMatch ? " — must match amount to refund" : null}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 shrink-0"
          onClick={openAddModal}
          disabled={disabled || (expectedTotal > 0 && remaining <= 0)}
        >
          <Plus className="h-3.5 w-3.5" />
          {addButtonLabel}
        </Button>
      </div>

      {lines.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 p-4 text-center text-xs text-muted-foreground">
          <Wallet className="mx-auto mb-2 h-5 w-5 opacity-50" />
          <p className="font-medium text-foreground/80">{emptyTitle}</p>
          <p className="mt-0.5">{emptyHint}</p>
        </div>
      ) : (
        <div className="divide-y divide-border/40 rounded-lg border border-border/40 bg-card overflow-hidden">
          {lines.map((line, index) => {
            const reference = salePaymentLineReference(line);
            return (
              <div
                key={line.clientId}
                className="flex flex-col gap-3 p-3 text-xs hover:bg-muted/10 transition-colors sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-foreground">
                      {paymentMethodLabel(line.paymentMethod)}
                    </span>
                    {line.bankName ? (
                      <span className="inline-flex items-center rounded-full border border-blue-500/20 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold text-blue-600 dark:text-blue-400">
                        {line.bankName}
                      </span>
                    ) : null}
                  </div>
                  {reference ? (
                    <p className="text-[11px] text-muted-foreground">{reference}</p>
                  ) : null}
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-border/30 pt-2 sm:border-t-0 sm:pt-0">
                  <div className="text-right sm:min-w-[7rem]">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Amount</p>
                    <p className="text-sm font-bold tabular-nums text-foreground">{formatCurrency(line.amount)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                      onClick={() => openEditModal(index)}
                      disabled={disabled}
                      aria-label="Edit payment line"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive/80 hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setRemoveIndex(index)}
                      disabled={disabled}
                      aria-label="Remove payment line"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SalePaymentLineDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        editing={editingIndex != null}
        line={modalLine}
        onLineChange={setModalLine}
        expectedTotal={expectedTotal}
        salePrice={salePrice}
        otherLinesTotal={modalOtherLinesTotal}
        disabled={disabled}
        onSave={saveModalLine}
      />

      <ConfirmAlertDialog
        open={removeIndex != null}
        onOpenChange={(open) => !open && setRemoveIndex(null)}
        title="Remove payment line?"
        description={removeDescription}
        cancelLabel="Keep"
        confirmLabel="Remove"
        onConfirm={confirmRemove}
      />
    </div>
  );
}
